<template>
  <div>
    <template
      v-if="mergedOptions.search === 'outside' "
    >
      <div
        class="flex  flex-col gap-3 sm:flex-row mb-3 justify-between relative "
      >
        <div class="flex flex-col w-full sm:flex-row gap-3 items-stretch  md:max-h-[44px]  sm:items-center">
          <label>

            <span class="sr-only"> Search this table</span>

            <AM3InputText
              id="search"
              v-model="searchQuery"
              :placeholder="mergedOptions.searchPlaceholder"
              name="search"
              type="text"
              data-1p-ignore
              :data-test-id="generateDataTestId(dataTestId, 'search-input')"

              @input="handleSearchInput"
            >
              <template #left>
                <AM3Icon
                  type="magnifying-glass"
                  size="md"
                  color="weak"
                />
              </template>
            </AM3InputText>
          </label>
          <slot name="search-buttons" />
        </div>

        <slot name="toolbar-buttons" />
      </div>
    </template>

    <!-- the actual borders/boundaries of the datalist component -->
    <div
      class="flex flex-col w-full h-full z-[5] text-sm font-normal relative"
      :class="bordered ? 'border rounded-md border-weak border-solid' : ''"
    >
      <slot name="before" />

      <!-- Toolbar with search and slot for additional buttons -->
      <div
        v-if="showToolbar && mergedOptions.search === 'inside'"
        class="w-full"
      >
        <label
          for="search"
          class="w-full [&:has(:focus-visible:not(button:focus-visible))]:outline-brand [&:has(input:hover)]:outline-brand-light relative z-[5] rounded-t-md outline-solid outline-offset-[-2px] outline-transparent outline outline-2 bg-surface-light px-4 flex flex-row items-center space-x-4"
        >
          <template v-if="mergedOptions.search === 'inside' ">
            <span class="pointer-events pt-1.5">
              <AM3Icon
                type="magnifying-glass"
                size="md"
                color="weak"
              />
            </span>
            <input
              id="search"
              v-model="searchQuery"
              :placeholder="mergedOptions.searchPlaceholder"
              name="search"
              type="text"
              data-1p-ignore
              class="w-full h-16 outline-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:border-0"
              :data-test-id="generateDataTestId(dataTestId, 'search-input')"
              @input="handleSearchInput"
            >
          </template>
          <!-- Slot for toolbar buttons -->
          <div class="mr-auto ml-0 flex flex-row items-center space-x-3 z-[15]">
            <slot name="toolbar-buttons" />
          </div>
        </label>
      </div>

      <!-- Table body with header and data rows -->
      <div
        ref="tableBodyRef"
        class="flex-1 overflow-y-auto rounded-md overflow-x-auto bg-surface-light"
        :class="mergedOptions?.showHeaderBackground ? '' : 'bg-surface-light px-4 pt-2 last:pb-2' "
      >
        <!-- Table header -->
        <div
          :class="mergedOptions?.showHeaderBackground ? 'bg-table-header-background border-b' : 'bg-surface-light' "
          class="sticky top-0 25 rounded-t-md font-bold  border-surfaces-neutral w-full"
        >
          <div
            class="flex w-full flex-row justify-between"
            :class=" mergedOptions?.showHeaderBackground ? '' : 'bg-surface-light  border-b border-solid border-surfaces-neutral' "
          >
            <!-- Checkbox column -->
            <label
              v-if="mergedOptions.checkboxes"
              v-tooltip.bottom="selectAllDisabled ? selectAllDisabledTooltip : undefined"
              :class="[
                mergedOptions?.showHeaderBackground ? 'bg-table-header-background' : 'bg-surface-light',
                selectAllDisabled ? 'cursor-not-allowed' : 'group cursor-pointer',
              ]"
              class="flex items-center justify-center min-w-[36px] w-[36px] sticky left-0 z-[4]  py-3"
            >
              <span class="sr-only">Select all rows</span>
              <AM3Checkbox
                v-model="allRowsSelected"
                :disabled="selectAllDisabled"
                :data-test-id="generateDataTestId(dataTestId, 'select-all')"
                @change="toggleAllRows"
              />
            </label>
            <!-- Column headers -->
            <!-- TODO should be using rem for width -->
            <div
              v-for="column in sortedColumns"
              :key="column.key"
              :data-align=" column.align "
              class="flex items-center w-full p-3 text-font-secondary"
              :class="[
                mergedOptions?.showHeaderBackground ? 'bg-table-header-background' : 'bg-surface-light',
                { 'sticky left-0 z-[10]': column.freeze },
                { 'shadow-[2px_0_5px_rgba(0,0,0,0.05)]': column.freeze && isScrolled },
                { 'cursor-pointer': column.sortable },
                { 'text-right [&>div]:justify-end': column.align === 'right' },

              ]"
              :style="getColumnStyle(column)"
              :data-test-id="generateDataTestId(dataTestId, 'column-header', column.key)"
              @click="column.sortable && handleSortColumn(column)"
            >
              <div
                class="flex items-center gap-1  h-full w-full group"
              >
                <span
                  :title="column.title"
                  class="text-ellipsis inline-block  text-nowrap overflow-hidden text-xs"
                >{{ column.title }}</span>
                <AM3Icon
                  v-if="column.sortable"
                  :type="sort?.key === column.key ? (sort?.order === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevron-up-down'"
                  size="sm"
                  class="transition-transform opacity-50 translate-x-1"
                  :class="{
                    'opacity-100 group-hover:opacity-100': column.sortable && sort?.key === column.key,
                    'group-hover:opacity-50': column.sortable && sort?.key !== column.key,
                  }"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- Skeleton Table rows when loading -->
        <div
          v-if="loading && rows.length === 0"
        >
          <div
            v-for="i in (skeletonRows ?? 8)"
            :key="i"
            class="flex min-h-[80px] w-full border-b bg-surface-light border-surfaces-neutral"
          >
            <!-- Skeleton checkbox -->
            <div
              v-if="mergedOptions.checkboxes"
              class="flex items-center justify-center min-w-[36px] w-[36px] sticky left-0 z-[11] bg-inherit cursor-pointer group"
            >
              <div class="w-4 h-4 bg-gray-200 rounded" />
            </div>
            <!-- Skeleton cells -->
            <div
              v-for="column in columns"
              :key="column.key"
              class="flex items-center  h-20 py-4 w-full"
              :style="getColumnStyle(column)"
            >
              <AM3LoadingContent
                v-if="column.key !== 'menu' "
                :lines="1"
                class="[&>div>div]:w-full [&>div]:pl-3 [&>div]:w-full w-full"
              />
            </div>
          </div>
        </div>
        <div v-else>
          <!-- Original rows code -->
          <div
            v-for="(row, rowIndex) in rows"
            :key="row && row[rowKey] != null ? row[rowKey] : rowIndex"
            class="flex group/row justify-between w-full last-of-type:rounded-md cursor-pointer bg-surfaces-white hover:bg-surfaces-informative border-b border-surfaces-neutral last:border-b-0 has-[.is-favorite]:bg-[#FACC1505] has-[.is-favorite]:hover:bg-surfaces-informative"
            :class="{ 'bg-surfaces-informative': isRowSelected(row) }"
            @click="handleRowClick(row, rowIndex, $event)"
          >
            <!-- Checkbox column -->
            <label
              v-if="mergedOptions.checkboxes"
              class="flex items-center justify-center min-w-[36px] w-[36px] sticky left-0 z-[11] bg-inherit cursor-pointer group"
              :class="checkboxClass"
            >
              <span class="sr-only">Select this row</span>
              <AM3Checkbox
                :model-value="isRowSelected(row)"
                :data-test-id="generateDataTestId(dataTestId, `row-${rowIndex}`, 'checkbox')"
                @change="value => toggleRowSelection(row, value)"
              />
            </label>

            <!-- Data cells -->
            <div
              v-for="column in sortedColumns"
              :key="column.key"
              :data-align=" column.align "
              class="flex items-center h-20 p-3"
              :class="[
                { 'sticky left-0 z-[10] bg-inherit': column.freeze },
                { 'shadow-[2px_0_5px_rgba(0,0,0,0.05)]': column.freeze && isScrolled },
                { 'text-right [&>div]:justify-end': column.align === 'right' },
              ]"
              :style="getColumnStyle(column)"
              :data-test-id="generateDataTestId(dataTestId, 'cell', `row-${rowIndex}--col-${column.key}`)"
            >
              <div
                v-overflow-tooltip="column.truncate ? String(row[column.key] ?? '') : undefined"
                class="w-full"
                :class="[
                  `cell-${column.key}`,
                  column.truncate ? 'truncate' : '',
                  column.align === 'right' ? 'text-right' : '',
                ]"
              >
                <slot
                  :name="`cell-${column.key}`"
                  :row="row"
                  :column="column"
                  :value="row[column.key]"
                  :data-test-id="generateDataTestId(dataTestId, 'cell', `row-${rowIndex}--col-${column.key}`)"
                >
                  <AM3Formatter
                    v-if="column.format"
                    :value="row[column.key]"
                    :type="column.format.type"
                    :options="column.format.options"
                  />
                  <span
                    v-else
                    class=""
                  >{{ row[column.key] }}</span>
                </slot>
              </div>
            </div>
          </div>
        </div>
        <!-- Loading indicator -->
        <!-- TODO fix loading should even when there are rows -->
        <div
          v-if="loading && !hasAfterSlot"
          class="flex items-center justify-center p-6 text-font-secondary"
        >
          <div
            class="w-5 h-5 border-2 border-border-strong border-t-buttons-primary-default rounded-full animate-spin mr-2"
          />
          <span>Loading more data...</span>
        </div>
        <!-- Empty state -->
        <div
          v-if="rows.length === 0 && !loading"
          class="flex items-center justify-center p-6 text-font-secondary"
        >
          <slot name="empty">
            <span :data-test-id="generateDataTestId(dataTestId, 'empty-indicator')">No data available</span>
          </slot>
        </div>
        <!-- Floating Action Panel -->
        <AM3FloatingActionPanel
          :visible="mergedOptions.checkboxes && mergedOptions.actions && selected.length >= (mergedOptions.actionPanelMinSelection ?? 1)"
          :actions="mergedOptions.actions"
          :selected-count="selected.length"
          :total-count="count"
          :select-all-value="allRowsSelected"
          :item-vocabulary="mergedOptions.itemVocabulary"
          :data-test-id="generateDataTestId(dataTestId, 'action-panel')"
          @action="handleAction"
          @unselect-all="toggleAllRows(false)"
        />
        <slot name="after" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { Comment, computed, onMounted, onUnmounted, ref, useSlots, watch } from 'vue'
import { debounce } from 'debounce'
import AM3Formatter from './AM3Formatter.vue'
import AM3FloatingActionPanel from './AM3FloatingActionPanel.vue'
import AM3Checkbox from '@/components/am3-generic/inputs/selection/AM3Checkbox.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'
import AM3LoadingContent from '@/components/am3-generic/base/AM3LoadingContent.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import type { Sort } from '@/types/sort'

const DEBOUNCE_DELAY = 300
const PAGE_SIZE = 50
const checkboxClass = 'data-list-checkbox'
const defaultOptions: DataListOptions = {
  search: false,
  searchPlaceholder: 'Search...',
  checkboxes: false,
  sortable: false,
  showHeaderBackground: true,
  itemVocabulary: ['item', 'items'],
}

// Props & models
const {
  rows,
  rowKey,
  columns,
  /**
   * Don't use directly, use {@link #mergedOptions} below
   */
  options,
  loading = false,
  count = 0,
  selectAllDisabled = false,
  selectAllDisabledTooltip = 'Available once the count loads',
  dataTestId = 'datalist',
  searchPlaceholder = 'Search...',
  search = '',
  bordered = true,
} = defineProps<{
  rows: T[]
  rowKey: Extract<keyof T, string>
  columns: DataListColumn<T>[]
  options?: DataListOptions
  loading?: boolean
  count?: number
  selectAllDisabled?: boolean
  selectAllDisabledTooltip?: string
  dataTestId?: string
  searchPlaceholder?: string
  skeletonRows?: number
  search?: string
  bordered?: boolean
}>()
const selected = defineModel<T[]>('selected', { required: true })
const sort = defineModel<Sort<T>>('sort')
const allRowsSelected = defineModel<boolean>('select-all', { default: false })

// Check for slots
const slots = useSlots()

// Handlers
const emit = defineEmits<{
  'row-click': [row: T, index: number]
  'search': [string]
  'load-more': []
  'action': [event: string]
  'select-all': [isAllSelected: boolean]
}>()

// Refs for component state
const tableBodyRef = ref<HTMLElement | null>(null)
const searchQuery = ref(search)

watch(() => search, (val) => {
  if (val !== searchQuery.value) {
    searchQuery.value = val
  }
})
const isScrolled = ref(false)
const showToolbar = computed(() => mergedOptions.value.search || slots['toolbar-buttons'])
// Checking `!!slots.after` alone would be true whenever the parent supplies the
// slot markup at all, even if a v-if inside it renders nothing (e.g. pagination
// hidden on a single page) — that would wrongly suppress the loading indicator.
const hasAfterSlot = computed(() => (slots.after?.() ?? []).some(node => node.type !== Comment))

// Computed properties:
const mergedOptions = computed((): DataListOptions => ({ ...defaultOptions, ...options }))
// Frozen columns first and set column.sortable
const sortedColumns = computed(() => columns
  .map<DataListColumn<T>>((col) => {
    col.sortable = !mergedOptions.value.sortable ? false : (col.sortable ?? mergedOptions.value.sortable)
    return col
  })
  .sort(a => a.freeze ? -1 : 0))

// Check if a row is selected
function isRowSelected(row: T) {
  if (!row || row[rowKey] == null) return false
  return selected.value.some(selectedRow => selectedRow[rowKey] === row[rowKey])
}

// Toggle selection of a single row
function toggleRowSelection(row: T, isSelected: boolean) {
  if (!row || row[rowKey] == null) return

  if (isSelected) {
    if (!isRowSelected(row)) {
      selected.value = [...selected.value, row]
    }
  }
  else {
    selected.value = selected.value.filter(selectedRow => selectedRow[rowKey] !== row[rowKey])
    allRowsSelected.value = false
    emit('select-all', false)
  }
}

// Toggle selection of all rows
function toggleAllRows(isAllSelected: boolean) {
  if (!isAllSelected) {
    selected.value = []
  }
  else {
    selected.value = [...rows]
  }
  allRowsSelected.value = isAllSelected
  emit('select-all', isAllSelected)
}

// Add watcher for rows
watch(() => rows, () => {
  if (allRowsSelected.value) {
    selected.value = [...rows]
  }
}, { deep: true })

// Handle row click
function handleRowClick(row: T, index: number, event: Event) {
  const element = event.target as Element
  if (element.closest(`.${checkboxClass}`)) {
    return
  }
  emit('row-click', row, index)
}

// Create debounced scroll handler
const handleScroll = debounce(() => {
  if (!tableBodyRef.value) return

  // Handle horizontal scroll for frozen columns
  isScrolled.value = tableBodyRef.value.scrollLeft > 0

  // Check if we're near the bottom of either the container or the page
  function isNearBottom() {
    const container = tableBodyRef.value
    if (!container) {
      return false
    }

    // Check container scroll - using a 200px threshold
    const containerBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200

    // Check page-content scroll - using a 200px threshold
    const pageContent = document.querySelector('.page-content')
    let pageContentBottom = false
    if (pageContent) {
      pageContentBottom = pageContent.scrollHeight - pageContent.scrollTop - pageContent.clientHeight < 200
    }

    // Only return true if we're actually near the bottom
    return (containerBottom && container.scrollHeight > container.clientHeight)
      || (pageContentBottom && pageContent && pageContent.scrollHeight > pageContent.clientHeight)
  }

  // Check if there's more data to load by comparing rows count to total count
  const hasMoreData = count > rows.length

  if (isNearBottom() && !loading && rows.length >= PAGE_SIZE && hasMoreData) {
    emit('load-more')
  }
}, DEBOUNCE_DELAY)

// Add page-content scroll listener
onMounted(() => {
  const pageContent = document.querySelector('.page-content')
  if (pageContent) {
    pageContent.addEventListener('scroll', handleScroll)
  }
})

onUnmounted(() => {
  const pageContent = document.querySelector('.page-content')
  if (pageContent) {
    pageContent.removeEventListener('scroll', handleScroll)
  }
})

// Handle search with debounce
const handleSearchInput = debounce(() => emit('search', searchQuery.value), DEBOUNCE_DELAY)

// Update the handleSortColumn function to match audience module format
function handleSortColumn(column: DataListColumn<T>) {
  if (column.sortable === false) {
    return
  }

  if (sort.value?.key === column.key) {
    // Toggle direction if same column
    const { key, order } = sort.value
    sort.value = {
      key,
      order: order === 'asc' ? 'desc' : 'asc',
    }
  }
  else {
    // Set new column and default to ascending
    sort.value = {
      key: column.key,
      order: 'asc',
    }
  }
}

// Handle action click
function handleAction(eventName: string | undefined) {
  if (eventName) {
    emit('action', eventName)
  }
}

// Column style computed property - using flexible widths to spread across the page
function getColumnStyle(column: DataListColumn<T>) {
  // First column should have a basis of 600px, others will share the remaining space
  const isFirstColumn = columns.indexOf(column) === 0
  const columnWidth = column.width // || (isFirstColumn ? 450 : 150)

  return {
    minWidth: column.width || '120px',
    width: column.width || '120px',
    maxWidth: column.width && !column.grow ? columnWidth + 'px' : 'none',
    flex: column.width ? `1 0 ${columnWidth}px` : '1',
    left: column.freeze ? (mergedOptions.value.checkboxes ? '50px' : '0px') : 'auto',
    marginLeft: column.key === 'menu' ? 'auto' : '1px', // this checks for the menu column and it, and everything on it's right, will be pushed to the right.
  }
}
</script>

<!-- EXPORTS -->
<script lang="ts">
export interface DataListAction {
  label: string
  icon: string
  event?: string
  subactions?: {
    label: string
    event: string
  }[]
}

export interface DataListOptions {
  search?: false | 'outside' | 'inside'
  searchPlaceholder?: string
  checkboxes?: boolean
  sortable?: boolean
  actions?: DataListAction[]
  itemVocabulary?: [string, string]
  // Fewest selected rows before the floating action panel appears (default 1).
  actionPanelMinSelection?: number
  showHeaderBackground?: boolean
}

export interface DataListColumn<RowType extends Record<string, any>> {
  key: Extract<keyof RowType, string>
  title: string
  freeze?: boolean
  width?: number
  // Keeps `width` as the minimum and lets the column take up any spare table width, which a plain `width` never does.
  grow?: boolean
  sortable?: boolean
  align?: 'right' | 'left' | 'center'
  truncate?: boolean
  format?: {
    // TODO add types for format options
    type: 'currency' | 'datetime' | 'relative-time' | 'number'
    options?: Record<string, any>
  }
}

export function filterOutNonExistingRows<RowType>(rowKey: keyof RowType, selected: RowType[], rows: RowType[]) {
  const rowsKeys = new Set(rows.map(row => row[rowKey]))
  return selected.filter(row => rowsKeys.has(row[rowKey]))
}
</script>

<style scoped>
/* Only keeping animation that can't be done with Tailwind */
@keyframes spin {
  to {
    transform: rotate360deg;
  }
}
</style>
